import { useState, useRef, useEffect, useCallback } from 'react';
import { presentationTemplates } from '../../mocks/presentationTemplates';
import type { PresentationTemplate } from '../../mocks/presentationTemplates';
import ToolsDropdown from '../../components/feature/ToolsDropdown';
import LoginModal from '../home/components/LoginModal';
import { useFreeTrialGate } from '../../hooks/useFreeTrialGate';
import UpgradeTrialModal from '../../components/feature/UpgradeTrialModal';
import PresentationStatsBar from './components/PresentationStatsBar';
import PresentationFeatureHighlights from './components/PresentationFeatureHighlights';
import PresentationTrendingTopics from './components/PresentationTrendingTopics';
import PresentationPublishBanner from './components/PresentationPublishBanner';
import SlidePreviewModal from './components/SlidePreviewModal';
import GenerateAIPromptBar from './components/GenerateAIPromptBar';
import CompareTemplatesModal from './components/CompareTemplatesModal';
import TemplateCardSkeleton from './components/TemplateCardSkeleton';
import FavoritesDrawer from './components/FavoritesDrawer';
import GenerateAIOutlineModal from './components/GenerateAIOutlineModal';

const CATEGORIES = [
  'All', 'Business', 'Marketing', 'Education', 'Sales', 'Creative',
  'Medical', 'Tech', 'Finance', 'Startup', 'HR', 'Non-Profit',
  'Science', 'Law', 'Real Estate', 'Fashion', 'Travel', 'Food & Beverage', 'Sports', 'Architecture',
];

const SLIDE_RANGES = [
  { label: 'Any length', min: 0, max: Infinity },
  { label: '5–10 slides', min: 5, max: 10 },
  { label: '10–20 slides', min: 10, max: 20 },
  { label: '20+ slides', min: 20, max: Infinity },
];

const SORT_OPTIONS = ['Most Popular', 'Newest', 'Trending', 'Most Slides'];
const TEMPLATES_PER_PAGE = 12;

const RECENTLY_VIEWED_KEY = 'pres-recently-viewed';
const MAX_RECENTLY_VIEWED = 10;

const INFOGRAPHIC_TYPES = [
  { id: 'all',          label: 'All Types',    icon: 'ri-layout-grid-2-line',     color: '#64748b' },
  { id: 'Timeline',     label: 'Timeline',     icon: 'ri-time-line',              color: '#0891b2' },
  { id: 'SWOT',         label: 'SWOT',         icon: 'ri-focus-3-line',           color: '#7c3aed' },
  { id: 'Dashboard',    label: 'Dashboard',    icon: 'ri-dashboard-2-line',       color: '#059669' },
  { id: 'Process Flow', label: 'Process Flow', icon: 'ri-git-branch-line',        color: '#d97706' },
  { id: 'Pyramid',      label: 'Pyramid',      icon: 'ri-bar-chart-grouped-line', color: '#e11d48' },
  { id: 'Comparison',   label: 'Comparison',   icon: 'ri-scales-3-line',          color: '#db2777' },
];

const COLOR_PALETTES = [
  { id: 'all',        label: 'All Colors',   icon: 'ri-palette-line',     swatches: ['#E63946', '#2A9D8F', '#0A2647', '#7C3AED', '#8D99AE'] },
  { id: 'Red/Orange', label: 'Red & Orange', icon: 'ri-fire-line',        swatches: ['#E63946', '#F77F00', '#FCBF49'] },
  { id: 'Teal/Green', label: 'Teal & Green', icon: 'ri-leaf-line',        swatches: ['#264653', '#2A9D8F', '#4ECDC4'] },
  { id: 'Navy/Blue',  label: 'Navy & Blue',  icon: 'ri-drop-line',        swatches: ['#0A2647', '#144272', '#2C74B3'] },
  { id: 'Purple',     label: 'Purple',       icon: 'ri-magic-line',       swatches: ['#7C3AED', '#C77DFF', '#F72585'] },
  { id: 'Neutral',    label: 'Graphite',     icon: 'ri-contrast-2-line',  swatches: ['#2B2D42', '#8D99AE', '#EDF2F4'] },
];

// ── Named palette groups ──────────────────────────────────────────
const PALETTE_GROUP_META: Record<string, { name: string; icon: string; accent: string }> = {
  'Red/Orange': { name: 'Warm Accent',    icon: 'ri-fire-line',        accent: '#E63946' },
  'Teal/Green': { name: 'Fresh Teal',     icon: 'ri-leaf-line',        accent: '#2A9D8F' },
  'Navy/Blue':  { name: 'Deep Navy',      icon: 'ri-drop-line',        accent: '#144272' },
  'Purple':     { name: 'Bold Purple',    icon: 'ri-magic-line',       accent: '#7C3AED' },
  'Neutral':    { name: 'Graphite',       icon: 'ri-contrast-2-line',  accent: '#475569' },
};

// ── detectInfographicType ─────────────────────────────────────────────────
function detectInfographicType(template: PresentationTemplate): { id: string; label: string; icon: string; color: string } {
  const text = `${template.name} ${template.tags.join(' ')} ${template.description}`.toLowerCase();
  if (text.includes('timeline') || text.includes('history') || text.includes('roadmap') || text.includes('milestone')) {
    return INFOGRAPHIC_TYPES.find(t => t.id === 'Timeline')!;
  }
  if (text.includes('swot') || text.includes('strategy') || text.includes('analysis') || text.includes('matrix')) {
    return INFOGRAPHIC_TYPES.find(t => t.id === 'SWOT')!;
  }
  if (text.includes('dashboard') || text.includes('kpi') || text.includes('metric') || text.includes('analytics') || text.includes('data')) {
    return INFOGRAPHIC_TYPES.find(t => t.id === 'Dashboard')!;
  }
  if (text.includes('process') || text.includes('flow') || text.includes('cycle') || text.includes('step') || text.includes('workflow')) {
    return INFOGRAPHIC_TYPES.find(t => t.id === 'Process Flow')!;
  }
  if (text.includes('pyramid') || text.includes('hierarchy') || text.includes('priority') || text.includes('level')) {
    return INFOGRAPHIC_TYPES.find(t => t.id === 'Pyramid')!;
  }
  if (text.includes('comparison') || text.includes('compare') || text.includes('versus') || text.includes(' vs ') || text.includes('side-by-side')) {
    return INFOGRAPHIC_TYPES.find(t => t.id === 'Comparison')!;
  }
  return { id: 'Infographic', label: 'Infographic', icon: 'ri-bar-chart-box-line', color: '#64748b' };
}

// ── detectPaletteGroup ────────────────────────────────────────────────────
function detectPaletteGroup(template: PresentationTemplate): string {
  const palette = template.colorPalette ?? [];
  if (palette.length === 0) return 'Neutral';
  const hex = palette[0].replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  if (b > 150 && r > 100 && g < 120) return 'Purple';
  if (b >= r && b >= g && b > 80 && r < 120) return 'Navy/Blue';
  if (g > r && g > b) return 'Teal/Green';
  if (r > g && r > b && r > 150) return 'Red/Orange';
  return 'Neutral';
}

// ── getReadTime ───────────────────────────────────────────────────────────
function getReadTime(slideCount: number): string {
  const minutes = Math.ceil(slideCount * 1.5);
  return `${minutes} min`;
}

// ── getAudienceType ───────────────────────────────────────────────────────
function getAudienceType(category: string): string {
  const map: Record<string, string> = {
    Business: 'Business Pros',
    Marketing: 'Marketers',
    Education: 'Educators',
    Sales: 'Sales Teams',
    Creative: 'Creatives',
    Medical: 'Healthcare',
    Tech: 'Tech Teams',
    Finance: 'Finance',
    Startup: 'Founders',
    HR: 'HR Teams',
    'Non-Profit': 'Non-Profits',
    Science: 'Researchers',
    Law: 'Legal Teams',
    'Real Estate': 'Realtors',
    Fashion: 'Designers',
    Travel: 'Travel Brands',
    'Food & Beverage': 'F&B Industry',
    Sports: 'Sports Orgs',
    Architecture: 'Architects',
  };
  return map[category] ?? 'All Audiences';
}

// ── COLLECTIONS ───────────────────────────────────────────────────────────
const COLLECTIONS = [
  { id: 'popular',   label: 'Trending Now',      sub: 'Most used this week',       icon: 'ri-fire-line',           color: '#e11d48', bg: '#fff1f2', filterCategory: null as string | null, filterBadge: 'Trending' as string | null },
  { id: 'new',       label: 'Fresh Arrivals',     sub: 'Brand new templates',       icon: 'ri-star-line',           color: '#7c3aed', bg: '#f5f3ff', filterCategory: null as string | null, filterBadge: 'New' as string | null },
  { id: 'business',  label: 'Business Suite',     sub: 'Corporate & pitch decks',   icon: 'ri-briefcase-4-line',    color: '#1d4ed8', bg: '#eff6ff', filterCategory: 'Business',            filterBadge: null as string | null },
  { id: 'marketing', label: 'Marketing Hub',      sub: 'Campaigns & brand kits',    icon: 'ri-megaphone-line',      color: '#059669', bg: '#ecfdf5', filterCategory: 'Marketing',           filterBadge: null as string | null },
  { id: 'education', label: 'Education Pack',     sub: 'Courses, lessons & more',   icon: 'ri-graduation-cap-line', color: '#d97706', bg: '#fffbeb', filterCategory: 'Education',           filterBadge: null as string | null },
  { id: 'startup',   label: 'Startup Toolkit',    sub: 'Pitches & investor decks',  icon: 'ri-rocket-2-line',       color: '#db2777', bg: '#fdf2f8', filterCategory: 'Startup',             filterBadge: null as string | null },
  { id: 'finance',   label: 'Finance & Reports',  sub: 'Charts, KPIs & analysis',   icon: 'ri-line-chart-line',     color: '#0891b2', bg: '#ecfeff', filterCategory: 'Finance',             filterBadge: null as string | null },
  { id: 'creative',  label: 'Creative Studio',    sub: 'Portfolios & visual work',  icon: 'ri-palette-line',        color: '#9333ea', bg: '#faf5ff', filterCategory: 'Creative',            filterBadge: null as string | null },
];

// ── Palette Swatch Tooltip ────────────────────────────────────────────────────
function PaletteSwatchTooltip({ template }: { template: PresentationTemplate }) {
  const [show, setShow] = useState(false);
  const group    = detectPaletteGroup(template);
  const meta     = PALETTE_GROUP_META[group] ?? { name: 'Custom Palette', icon: 'ri-palette-line', accent: '#64748b' };

  return (
    <div
      className="relative flex gap-1"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {/* Swatch dots */}
      {template.colorPalette.slice(0, 3).map((c, i) => (
        <div
          key={i}
          className="w-3.5 h-3.5 rounded-full border-2 border-white cursor-pointer transition-transform hover:scale-125"
          style={{ backgroundColor: c }}
        ></div>
      ))}

      {/* Floating tooltip */}
      {show && (
        <div
          className="absolute bottom-full left-0 mb-2.5 z-[999] pointer-events-none"
          style={{ minWidth: '178px' }}
        >
          <div className="bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-white/10">
            {/* Header */}
            <div
              className="flex items-center gap-2 px-3 py-2.5"
              style={{ background: `linear-gradient(135deg, ${meta.accent}22, ${meta.accent}11)`, borderBottom: `1px solid ${meta.accent}33` }}
            >
              <div
                className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: meta.accent }}
              >
                <i className={`${meta.icon} text-white text-xs`}></i>
              </div>
              <div>
                <p className="text-xs font-extrabold text-white leading-none">{meta.name} Palette</p>
                <p className="text-[9px] text-slate-400 mt-0.5">Infograpia</p>
              </div>
            </div>

            {/* Hex values */}
            <div className="px-3 py-2.5 space-y-1.5">
              {template.colorPalette.map((hex, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded-md flex-shrink-0 border border-white/10"
                    style={{ backgroundColor: hex }}
                  ></div>
                  <span className="text-xs font-mono text-slate-300 uppercase tracking-wide">{hex}</span>
                  <span className="text-[9px] text-slate-500 ml-auto">
                    {i === 0 ? 'Primary' : i === 1 ? 'Secondary' : 'Accent'}
                  </span>
                </div>
              ))}
            </div>

            {/* Footer swatch bar */}
            <div className="flex h-2">
              {template.colorPalette.map((c, i) => (
                <div key={i} className="flex-1" style={{ backgroundColor: c }}></div>
              ))}
            </div>
          </div>
          {/* Arrow */}
          <div className="ml-3 w-0 h-0" style={{
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '5px solid rgb(15 23 42)',
          }}></div>
        </div>
      )}
    </div>
  );
}

// ── Collections Shelf ─────────────────────────────────────────────────────
function CollectionsShelf({
  allTemplates,
  onApplyCollection,
}: {
  allTemplates: PresentationTemplate[];
  onApplyCollection: (filterCategory: string | null, filterBadge: string | null) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 10);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) { el.addEventListener('scroll', checkScroll); checkScroll(); }
    return () => { el?.removeEventListener('scroll', checkScroll); };
  }, []);

  const scroll = (dir: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: dir === 'right' ? 320 : -320, behavior: 'smooth' });
    setTimeout(checkScroll, 350);
  };

  const getCount = (col: typeof COLLECTIONS[number]) => {
    return allTemplates.filter(t => {
      if (col.filterCategory && t.category !== col.filterCategory) return false;
      if (col.filterBadge && t.badge !== col.filterBadge) return false;
      return true;
    }).length;
  };

  const getThumbs = (col: typeof COLLECTIONS[number]): string[] => {
    return allTemplates
      .filter(t => {
        if (col.filterCategory && t.category !== col.filterCategory) return false;
        if (col.filterBadge && t.badge !== col.filterBadge) return false;
        return true;
      })
      .slice(0, 3)
      .map(t => t.thumbnail);
  };

  const handleClick = (col: typeof COLLECTIONS[number]) => {
    const newActive = activeId === col.id ? null : col.id;
    setActiveId(newActive);
    if (newActive === null) {
      onApplyCollection(null, null);
    } else {
      onApplyCollection(col.filterCategory, col.filterBadge);
    }
  };

  return (
    <div className="mb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 flex items-center justify-center bg-gradient-to-br from-rose-500 to-fuchsia-500 rounded-lg">
            <i className="ri-bookmark-3-fill text-white text-sm"></i>
          </div>
          <div>
            <h3 className="text-sm font-extrabold text-slate-800 leading-none">Collections</h3>
            <p className="text-[11px] text-slate-400 mt-0.5">Curated picks for every use case</p>
          </div>
          {activeId && (
            <button
              onClick={() => { setActiveId(null); onApplyCollection(null, null); }}
              className="ml-2 flex items-center gap-1 px-2.5 py-1 bg-rose-50 text-rose-500 text-xs font-semibold rounded-full border border-rose-200 hover:bg-rose-100 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-close-line text-xs"></i>Clear
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => scroll('left')}
            disabled={!canLeft}
            className="w-7 h-7 bg-white border border-slate-200 rounded-full flex items-center justify-center cursor-pointer hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <i className="ri-arrow-left-s-line text-slate-600 text-sm"></i>
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canRight}
            className="w-7 h-7 bg-white border border-slate-200 rounded-full flex items-center justify-center cursor-pointer hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <i className="ri-arrow-right-s-line text-slate-600 text-sm"></i>
          </button>
        </div>
      </div>

      {/* Horizontal scroll */}
      <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
        {COLLECTIONS.map(col => {
          const count = getCount(col);
          const thumbs = getThumbs(col);
          const isActive = activeId === col.id;
          return (
            <button
              key={col.id}
              onClick={() => handleClick(col)}
              className={`flex-shrink-0 w-52 rounded-2xl overflow-hidden border-2 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 text-left ${
                isActive
                  ? 'border-transparent ring-2 ring-offset-1'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              style={isActive ? { ringColor: col.color, borderColor: col.color } as React.CSSProperties : {}}
            >
              {/* Thumbnail strip */}
              <div className="relative h-20 overflow-hidden flex">
                {thumbs.length > 0 ? (
                  thumbs.map((src, i) => (
                    <div
                      key={i}
                      className="flex-1 overflow-hidden"
                      style={{ marginLeft: i > 0 ? '1px' : undefined }}
                    >
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))
                ) : (
                  <div className="flex-1 flex items-center justify-center" style={{ backgroundColor: col.bg }}>
                    <i className={`${col.icon} text-2xl`} style={{ color: col.color }}></i>
                  </div>
                )}
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                {/* Active checkmark */}
                {isActive && (
                  <div
                    className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: col.color }}
                  >
                    <i className="ri-check-line text-white text-[10px]"></i>
                  </div>
                )}
                {/* Count badge */}
                <div
                  className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold text-white"
                  style={{ backgroundColor: `${col.color}cc` }}
                >
                  {count} templates
                </div>
              </div>

              {/* Info */}
              <div className="px-3 py-2.5" style={{ backgroundColor: isActive ? col.bg : 'white' }}>
                <div className="flex items-center gap-2 mb-1">
                  <div
                    className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: col.color }}
                  >
                    <i className={`${col.icon} text-white text-[10px]`}></i>
                  </div>
                  <p
                    className="text-xs font-extrabold leading-tight line-clamp-1"
                    style={{ color: isActive ? col.color : '#0f172a' }}
                  >
                    {col.label}
                  </p>
                </div>
                <p className="text-[11px] text-slate-400 ml-7">{col.sub}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TemplateCard({
  template,
  onPreview,
  onUse,
  onCompareToggle,
  isComparing,
  isFavorited,
  onFavoriteToggle,
}: {
  template: PresentationTemplate;
  onPreview: () => void;
  onUse: () => void;
  onCompareToggle: () => void;
  isComparing: boolean;
  isFavorited: boolean;
  onFavoriteToggle: () => void;
}) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const badgeColors: Record<string, string> = {
    Popular: 'bg-amber-100 text-amber-700',
    New: 'bg-emerald-100 text-emerald-700',
    Trending: 'bg-rose-100 text-rose-700',
  };

  const infoType = detectInfographicType(template);

  return (
    <div
      className={`group bg-white rounded-2xl border-2 transition-all duration-300 overflow-hidden cursor-pointer hover:-translate-y-1 ${
        isComparing ? 'border-rose-500 ring-2 ring-rose-200' : 'border-slate-200 hover:border-rose-400'
      }`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden bg-slate-100">
        {/* Shimmer skeleton shown while image loads */}
        {!imgLoaded && (
          <div className="absolute inset-0 overflow-hidden bg-slate-100">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
          </div>
        )}
        <img
          src={template.thumbnail}
          alt={template.name}
          onLoad={() => setImgLoaded(true)}
          onClick={onUse}
          className={`w-full h-full object-cover group-hover:scale-105 transition-all duration-500 ${
            imgLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        {/* ── Infographic Type badge — slides in on hover ── */}
        <div className="absolute top-0 left-0 right-0 flex justify-center pointer-events-none">
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-b-xl text-xs font-bold text-white translate-y-[-100%] group-hover:translate-y-0 transition-transform duration-300 ease-out shadow-lg"
            style={{ backgroundColor: infoType.color }}
          >
            <i className={`${infoType.icon} text-xs`}></i>
            {infoType.label}
          </div>
        </div>
        {/* Badges */}
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
          <span className="px-2 py-1 bg-white/90 backdrop-blur-sm text-slate-700 text-xs font-bold rounded-lg border border-white/50">
            {template.category}
          </span>
          {template.badge && (
            <span className={`px-2 py-1 text-xs font-bold rounded-lg ${badgeColors[template.badge]}`}>
              {template.badge}
            </span>
          )}
        </div>
        {/* Heart button — always visible when favorited, visible on hover otherwise */}
        <button
          onClick={e => { e.stopPropagation(); onFavoriteToggle(); }}
          className={`absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center cursor-pointer transition-all ${
            isFavorited
              ? 'bg-rose-500 opacity-100'
              : 'bg-white/90 backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:bg-rose-50'
          }`}
          title={isFavorited ? 'Remove from favorites' : 'Save to favorites'}
        >
          <i className={`text-sm ${isFavorited ? 'ri-heart-fill text-white' : 'ri-heart-line text-slate-500 hover:text-rose-500'}`}></i>
        </button>
        {/* Compare checkbox */}
        <div className="absolute bottom-2.5 right-2.5">
          <button
            onClick={e => { e.stopPropagation(); onCompareToggle(); }}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap border ${
              isComparing
                ? 'bg-rose-500 text-white border-rose-500'
                : 'bg-white/90 backdrop-blur-sm text-slate-700 border-white/50 opacity-0 group-hover:opacity-100'
            }`}
            title={isComparing ? 'Remove from compare' : 'Add to compare'}
          >
            <i className={`text-xs ${isComparing ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'}`}></i>
            Compare
          </button>
        </div>
        {/* Hover action buttons */}
        <div className="absolute bottom-2.5 left-2.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ right: '90px' }}>
          <div className="flex gap-1.5">
            <button
              onClick={e => { e.stopPropagation(); onPreview(); }}
              className="py-1.5 px-2.5 bg-white/90 backdrop-blur-sm rounded-xl text-xs font-bold text-slate-700 flex items-center justify-center gap-1.5 cursor-pointer hover:bg-white transition-colors whitespace-nowrap"
            >
              <i className="ri-eye-line text-slate-600"></i>Preview
            </button>
            <button
              onClick={e => { e.stopPropagation(); onUse(); }}
              className="py-1.5 px-2.5 bg-rose-500 rounded-xl text-xs font-bold text-white flex items-center justify-center gap-1.5 cursor-pointer hover:bg-rose-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-magic-line"></i>Use
            </button>
          </div>
        </div>
      </div>

      {/* ── Card info ── */}
      <div className="p-4" onClick={onUse}>
        {/* ── PERMANENT infographic type badge + heart ── */}
        <div className="flex items-center gap-1.5 mb-2">
          <div
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold text-white flex-shrink-0"
            style={{ backgroundColor: infoType.color }}
          >
            <i className={`${infoType.icon} text-[10px]`}></i>
            <span>{infoType.label}</span>
          </div>
          {template.badge && (
            <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${badgeColors[template.badge]}`}>
              {template.badge}
            </span>
          )}
          {isFavorited && (
            <span className="ml-auto flex-shrink-0 flex items-center gap-0.5 text-[10px] font-bold text-rose-500">
              <i className="ri-heart-fill text-xs"></i>Saved
            </span>
          )}
        </div>

        <h3 className="font-bold text-slate-900 text-sm group-hover:text-rose-600 transition-colors leading-snug line-clamp-2 mb-1.5">
          {template.name}
        </h3>
        <p className="text-xs text-slate-500 line-clamp-2 mb-2.5 leading-relaxed">{template.description}</p>

        {/* ── Quick Stats bar ── */}
        <div className="flex items-center gap-2 px-2.5 py-2 bg-slate-50 rounded-xl mb-3 border border-slate-100">
          <div className="flex items-center gap-1 flex-shrink-0">
            <i className="ri-time-line text-rose-400 text-xs"></i>
            <span className="text-xs font-bold text-slate-700">{getReadTime(template.slideCount)}</span>
            <span className="text-[10px] text-slate-400">read</span>
          </div>
          <div className="w-px h-3 bg-slate-200 flex-shrink-0"></div>
          <div className="flex items-center gap-1 min-w-0">
            <i className="ri-group-line text-teal-500 text-xs flex-shrink-0"></i>
            <span className="text-[11px] text-slate-500 font-medium truncate">{getAudienceType(template.category)}</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 text-xs text-slate-400">
            <span className="flex items-center gap-1 flex-shrink-0">
              <i className="ri-layout-grid-line text-slate-300"></i>{template.slideCount} slides
            </span>
            {/* ── Palette swatches with hover tooltip ── */}
            <PaletteSwatchTooltip template={template} />
          </div>
          <div className="flex gap-1 flex-wrap justify-end">
            {template.tags.slice(0, 1).map(tag => (
              <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full font-medium">{tag}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Section entrance animation hook ──────────────────────────────────────────
function useSectionEntrance(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setTimeout(() => setVisible(true), delay); obs.disconnect(); } },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);

  return { ref, visible };
}

// ── Recently Viewed Shelf ─────────────────────────────────────────────────────
function RecentlyViewedShelf({
  ids,
  allTemplates,
  onPreview,
  onUse,
  onClear,
}: {
  ids: string[];
  allTemplates: PresentationTemplate[];
  onPreview: (t: PresentationTemplate) => void;
  onUse: () => void;
  onClear: () => void;
}) {
  const templates = ids
    .map(id => allTemplates.find(t => t.id === id))
    .filter((t): t is PresentationTemplate => Boolean(t));

  if (templates.length === 0) return null;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <i className="ri-history-line text-rose-400 text-sm"></i>
          <h3 className="text-sm font-extrabold text-slate-700 uppercase tracking-wider">Recently Viewed</h3>
          <span className="px-2 py-0.5 bg-rose-50 text-rose-500 text-xs font-bold rounded-full border border-rose-100">{templates.length}</span>
        </div>
        <button
          onClick={onClear}
          className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 cursor-pointer transition-colors whitespace-nowrap"
        >
          <i className="ri-close-line text-xs"></i>Clear history
        </button>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
        {templates.map(t => (
          <div
            key={t.id}
            className="flex-shrink-0 w-52 bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-rose-300 transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group"
          >
            <div className="relative h-28 overflow-hidden bg-slate-100">
              <img
                src={t.thumbnail}
                alt={t.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              <div className="absolute bottom-0 left-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-1.5">
                <button
                  onClick={e => { e.stopPropagation(); onPreview(t); }}
                  className="flex-1 py-1.5 bg-white/90 backdrop-blur-sm rounded-lg text-xs font-bold text-slate-700 cursor-pointer hover:bg-white transition-colors whitespace-nowrap"
                >
                  Preview
                </button>
                <button
                  onClick={e => { e.stopPropagation(); onUse(); }}
                  className="flex-1 py-1.5 bg-rose-500 rounded-lg text-xs font-bold text-white cursor-pointer hover:bg-rose-600 transition-colors whitespace-nowrap"
                >
                  Use
                </button>
              </div>
              <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-white/90 backdrop-blur-sm rounded-md">
                <span className="text-xs font-medium text-slate-600">{t.slideCount} slides</span>
              </div>
              {t.badge && (
                <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-rose-500 rounded-md">
                  <span className="text-xs font-bold text-white">{t.badge}</span>
                </div>
              )}
            </div>
            <div className="p-2.5" onClick={() => onPreview(t)}>
              <p className="text-xs font-bold text-slate-800 truncate">{t.name}</p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-slate-400 truncate">{t.category}</p>
                <div className="flex gap-0.5 flex-shrink-0">
                  {t.colorPalette.slice(0, 3).map((c, i) => (
                    <div key={i} className="w-10 h-10 rounded-full" style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const FAVORITES_KEY = 'pres-favorites';

export default function PresentationCreator() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('Most Popular');
  const [currentPage, setCurrentPage] = useState(1);
  const [slideRangeIdx, setSlideRangeIdx] = useState(0);
  const [selectedInfographic, setSelectedInfographic] = useState('all');
  const [selectedPalette, setSelectedPalette] = useState('all');
  const [collectionCategory, setCollectionCategory] = useState<string | null>(null);
  const [collectionBadge, setCollectionBadge] = useState<string | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<{ id: string; file: File; preview?: string }[]>([]);
  const [showAttach, setShowAttach] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isGridLoading, setIsGridLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState<'templates' | 'features' | null>(null);
  const templatesSectionRef = useRef<HTMLDivElement>(null);
  const categoriesRef = useRef<HTMLDivElement>(null);
  const [catCanLeft, setCatCanLeft] = useState(false);
  const [catCanRight, setCatCanRight] = useState(true);
  const featuresRef = useRef<HTMLDivElement>(null);

  // Section entrance hooks
  const statsEntrance = useSectionEntrance(0);
  const featuresEntrance = useSectionEntrance(100);
  const trendingEntrance = useSectionEntrance(80);
  const bannerEntrance = useSectionEntrance(120);

  // New feature states
  const [previewTemplate, setPreviewTemplate] = useState<PresentationTemplate | null>(null);
  const [compareList, setCompareList] = useState<string[]>([]);
  const [showCompareModal, setShowCompareModal] = useState(false);

  // ── Favorites ─────────────────────────────────────────────────────────────
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(FAVORITES_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [showFavoritesDrawer, setShowFavoritesDrawer] = useState(false);

  const toggleFavorite = useCallback((id: string) => {
    setFavorites(prev => {
      const updated = prev.includes(id) ? prev.filter(x => x !== id) : [id, ...prev];
      try { localStorage.setItem(FAVORITES_KEY, JSON.stringify(updated)); } catch { /* noop */ }
      return updated;
    });
  }, []);

  // ── AI Outline Modal ──────────────────────────────────────────────────────
  const [showAIOutlineModal, setShowAIOutlineModal] = useState(false);

  const checkCatScroll = useCallback(() => {
    const el = categoriesRef.current;
    if (!el) return;
    setCatCanLeft(el.scrollLeft > 10);
    setCatCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => {
    const el = categoriesRef.current;
    if (el) { el.addEventListener('scroll', checkCatScroll); checkCatScroll(); }
    return () => { el?.removeEventListener('scroll', checkCatScroll); };
  }, [checkCatScroll]);

  const scrollCat = (dir: 'left' | 'right') => {
    categoriesRef.current?.scrollBy({ left: dir === 'right' ? 300 : -300, behavior: 'smooth' });
    setTimeout(checkCatScroll, 350);
  };

  const handleApplyCollection = (filterCategory: string | null, filterBadge: string | null) => {
    setCollectionCategory(filterCategory);
    setCollectionBadge(filterBadge);
    setCurrentPage(1);
    setIsGridLoading(true);
    setTimeout(() => { setIsGridLoading(false); scrollToTemplates(); }, 450);
  };

  const filteredTemplates = presentationTemplates.filter(t => {
    const matchSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCat = selectedCategory === 'All' || t.category === selectedCategory;
    const range = SLIDE_RANGES[slideRangeIdx];
    const matchRange = t.slideCount >= range.min && t.slideCount <= range.max;
    const matchInfoType = selectedInfographic === 'all' ||
      detectInfographicType(t).label === selectedInfographic ||
      (t.name + ' ' + t.tags.join(' ') + ' ' + t.description).toLowerCase().includes(selectedInfographic.toLowerCase());
    const matchPalette = selectedPalette === 'all' || detectPaletteGroup(t) === selectedPalette;
    const matchCollection =
      (!collectionCategory || t.category === collectionCategory) &&
      (!collectionBadge || t.badge === collectionBadge);
    return matchSearch && matchCat && matchRange && matchInfoType && matchPalette && matchCollection;
  });

  const totalPages = Math.ceil(filteredTemplates.length / TEMPLATES_PER_PAGE);
  const startIdx = (currentPage - 1) * TEMPLATES_PER_PAGE;
  const currentTemplates = filteredTemplates.slice(startIdx, startIdx + TEMPLATES_PER_PAGE);

  const handleCompareToggle = (id: string) => {
    setCompareList(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const compareTemplates = presentationTemplates.filter(t => compareList.includes(t.id));

  const addFiles = (files: File[]) => {
    const valid = files.filter(f => f.size <= 25 * 1024 * 1024);
    if (attachedFiles.length + valid.length > 10) return;
    const newFiles = valid.map(file => {
      const item = { id: Math.random().toString(36).slice(2), file, preview: undefined as string | undefined };
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = e => setAttachedFiles(prev => prev.map(f => f.id === item.id ? { ...f, preview: e.target?.result as string } : f));
        reader.readAsDataURL(file);
      }
      return item;
    });
    setAttachedFiles(prev => [...prev, ...newFiles]);
  };

  const triggerTransition = useCallback((target: 'templates' | 'features') => {
    setTransitionTarget(target);
    setIsTransitioning(true);
    setTimeout(() => {
      if (target === 'templates') {
        templatesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        featuresRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setTimeout(() => {
        setIsTransitioning(false);
        setTransitionTarget(null);
      }, 550);
    }, 180);
  }, []);

  const scrollToTemplates = () => triggerTransition('templates');
  const scrollToFeatures = () => triggerTransition('features');

  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
    setIsGridLoading(true);
    setTimeout(() => { setIsGridLoading(false); scrollToTemplates(); }, 500);
  };

  const handleSlideRangeChange = (idx: number) => {
    setSlideRangeIdx(idx);
    setCurrentPage(1);
    setIsGridLoading(true);
    setTimeout(() => { setIsGridLoading(false); }, 420);
  };

  const handleInfographicTypeChange = (typeId: string) => {
    setSelectedInfographic(typeId);
    setCurrentPage(1);
    setIsGridLoading(true);
    setTimeout(() => { setIsGridLoading(false); }, 420);
  };

  const handlePaletteChange = (paletteId: string) => {
    setSelectedPalette(paletteId);
    setCurrentPage(1);
    setIsGridLoading(true);
    setTimeout(() => { setIsGridLoading(false); }, 420);
  };

  const handleTopicSearch = (topic: string) => {
    setSearchQuery(topic);
    setSelectedCategory('All');
    setCurrentPage(1);
    setIsGridLoading(true);
    setTimeout(() => { setIsGridLoading(false); scrollToTemplates(); }, 450);
  };

  const formatSize = (b: number) => b < 1048576 ? (b / 1024).toFixed(1) + ' KB' : (b / 1048576).toFixed(1) + ' MB';

  // Initial skeleton on mount
  useEffect(() => {
    const t = setTimeout(() => setIsGridLoading(false), 900);
    return () => clearTimeout(t);
  }, []);

  const [recentlyViewed, setRecentlyViewed] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(RECENTLY_VIEWED_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const addToRecentlyViewed = useCallback((id: string) => {
    setRecentlyViewed(prev => {
      const updated = [id, ...prev.filter(x => x !== id)].slice(0, MAX_RECENTLY_VIEWED);
      try { localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(updated)); } catch { /* noop */ }
      return updated;
    });
  }, []);

  const clearRecentlyViewed = useCallback(() => {
    setRecentlyViewed([]);
    try { localStorage.removeItem(RECENTLY_VIEWED_KEY); } catch { /* noop */ }
  }, []);

  // ── Active filters computation ─────────────────────────────────────────────
  type ActiveFilterChip = { key: string; label: string; icon: string; onClear: () => void };
  const activeFilterChips: ActiveFilterChip[] = [];
  if (searchQuery) activeFilterChips.push({ key: 'search', label: `"${searchQuery}"`, icon: 'ri-search-line', onClear: () => { setSearchQuery(''); setCurrentPage(1); } });
  if (selectedCategory !== 'All') activeFilterChips.push({ key: 'category', label: selectedCategory, icon: 'ri-folder-line', onClear: () => handleCategoryChange('All') });
  if (slideRangeIdx !== 0) activeFilterChips.push({ key: 'slides', label: SLIDE_RANGES[slideRangeIdx].label, icon: 'ri-layout-grid-line', onClear: () => handleSlideRangeChange(0) });
  if (selectedInfographic !== 'all') {
    const iType = INFOGRAPHIC_TYPES.find(t => t.id === selectedInfographic);
    if (iType) activeFilterChips.push({ key: 'infographic', label: iType.label, icon: iType.icon, onClear: () => handleInfographicTypeChange('all') });
  }
  if (selectedPalette !== 'all') {
    const pType = COLOR_PALETTES.find(p => p.id === selectedPalette);
    if (pType) activeFilterChips.push({ key: 'palette', label: pType.label, icon: pType.icon, onClear: () => handlePaletteChange('all') });
  }
  if (collectionCategory || collectionBadge) {
    const colLabel = collectionCategory ?? collectionBadge ?? 'Collection';
    activeFilterChips.push({ key: 'collection', label: colLabel, icon: 'ri-bookmark-3-line', onClear: () => handleApplyCollection(null, null) });
  }

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
    setSlideRangeIdx(0);
    setSelectedInfographic('all');
    setSelectedPalette('all');
    setCollectionCategory(null);
    setCollectionBadge(null);
    setCurrentPage(1);
    setIsGridLoading(true);
    setTimeout(() => setIsGridLoading(false), 420);
  };

  const { exhausted: trialExhausted, consume: consumeTrial, remaining: trialRemaining, dots: trialDots, used: trialUsed } = useFreeTrialGate('presentation-creator');
  const [showUpgradeTrial, setShowUpgradeTrial] = useState(false);

  /** Call this instead of setShowLogin(true) for "use template" and "create" actions */
  const handleUseAction = useCallback(() => {
    if (trialExhausted) {
      setShowUpgradeTrial(true);
      return;
    }
    consumeTrial();
    setShowLogin(true);
  }, [trialExhausted, consumeTrial]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-rose-50/40 to-fuchsia-50/40 pb-28">

      {/* ── Page Transition Overlay ──────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 z-[9999] pointer-events-none transition-opacity duration-300 ${isTransitioning ? 'opacity-100' : 'opacity-0'}`}
        aria-hidden="true"
      >
        {/* Directional gradient sweep */}
        <div
          className={`absolute inset-0 transition-transform duration-[550ms] ease-out ${isTransitioning ? 'translate-x-full' : '-translate-x-full'}`}
          style={{
            background: transitionTarget === 'features'
              ? 'linear-gradient(90deg, transparent 0%, rgba(168,85,247,0.12) 40%, rgba(244,63,94,0.10) 70%, transparent 100%)'
              : 'linear-gradient(90deg, transparent 0%, rgba(244,63,94,0.12) 40%, rgba(192,38,211,0.10) 70%, transparent 100%)',
            animationTimingFunction: 'cubic-bezier(0.22,1,0.36,1)',
          }}
        />
        {/* Top edge flash */}
        <div
          className={`absolute top-0 left-0 right-0 h-0.5 transition-all duration-[550ms] ease-out ${isTransitioning ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}
          style={{
            background: transitionTarget === 'features'
              ? 'linear-gradient(90deg, transparent, rgba(168,85,247,0.8), transparent)'
              : 'linear-gradient(90deg, transparent, rgba(244,63,94,0.8), transparent)',
            transformOrigin: 'left',
          }}
        />
      </div>

      {/* ── Header ─── */}
      <header className={`sticky top-0 z-40 transition-all duration-300 ${isHeaderScrolled ? 'bg-white/95 backdrop-blur-md border-b border-slate-200' : 'bg-white/80 backdrop-blur-sm border-b border-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => window.REACT_APP_NAVIGATE('/')} className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer">
                <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-fuchsia-500 rounded-xl flex items-center justify-center">
                  <i className="ri-slideshow-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-fuchsia-600 bg-clip-text text-transparent">openclaw.ai</div>
                  <div className="text-xs text-slate-400 mt-0.5">Presentation Creator</div>
                </div>
              </button>
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 rounded-full">
                <div className="w-2 h-2 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">OpenClaw Algorithm</span>
              </div>
              <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-full">
                <i className="ri-slideshow-line text-rose-500 text-sm"></i>
                <span className="text-xs font-medium text-slate-700">5M+ Presentations Made</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Favorites button in header */}
              <button
                onClick={() => setShowFavoritesDrawer(true)}
                className="relative flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-rose-600 px-3 py-2 rounded-lg hover:bg-rose-50 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-heart-line text-sm"></i>
                Favorites
                {favorites.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-rose-500 text-white text-[9px] font-extrabold rounded-full flex items-center justify-center">
                    {favorites.length > 9 ? '9+' : favorites.length}
                  </span>
                )}
              </button>
              {/* Trial indicator */}
              {!trialExhausted ? (
                <div className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 border border-rose-200 rounded-full">
                  <div className="flex gap-1">
                    {trialDots.map((d, i) => (
                      <div key={i} className={`w-1.5 h-1.5 rounded-full ${d.active ? 'bg-gray-300' : 'bg-rose-500'}`} />
                    ))}
                  </div>
                  <span className="text-[10px] font-semibold text-rose-700">{trialRemaining} free use{trialRemaining !== 1 ? 's' : ''}</span>
                </div>
              ) : (
                <button onClick={() => setShowUpgradeTrial(true)} className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-bold rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">
                  <i className="ri-sparkling-2-fill text-xs"></i>Upgrade
                </button>
              )}
              <button onClick={() => setShowLogin(true)} className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-slate-100 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-star-line text-sm"></i>View Features
              </button>
              <button onClick={() => setShowLogin(true)} className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-slate-100 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-layout-grid-line text-sm"></i>Browse Templates
              </button>
              <ToolsDropdown isDarkMode={false} />
              <button onClick={() => setShowLogin(true)} className="text-sm font-semibold px-4 py-2 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 transition-all whitespace-nowrap cursor-pointer">
                Sign In
              </button>
              <button onClick={handleUseAction} className="text-sm font-bold px-5 py-2.5 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white hover:opacity-90 transition-all whitespace-nowrap cursor-pointer">
                Create Free
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-rose-600 via-pink-600 to-fuchsia-700 text-white">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30" />
        {/* Decorative background shapes */}
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <div className="absolute top-8 right-8 w-72 h-72 bg-white rounded-2xl rotate-12 transform"></div>
          <div className="absolute top-24 right-32 w-48 h-32 bg-fuchsia-300 rounded-xl -rotate-6 transform"></div>
          <div className="absolute top-16 right-52 w-32 h-48 bg-pink-200 rounded-xl rotate-3 transform"></div>
        </div>
        {/* Slide preview mockups */}
        <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden xl:flex gap-3 items-center opacity-80">
          {[
            { seq: 'hero-slide-1', rotate: '-rotate-6', z: 'z-10', scale: 'scale-95' },
            { seq: 'hero-slide-2', rotate: 'rotate-2', z: 'z-20', scale: 'scale-100' },
            { seq: 'hero-slide-3', rotate: 'rotate-6', z: 'z-10', scale: 'scale-95' },
          ].map((s, i) => (
            <div key={i} className={`${s.rotate} ${s.z} ${s.scale} w-52 h-32 rounded-xl overflow-hidden border-2 border-white/30`}>
              <img
                src={`https://public.readdy.ai/ai/img_res/f78356c006d9600aa0f3f30954545bf1.jpg${s.seq}&orientation=landscape`}
                alt="slide preview"
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl w-full">
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <div className="flex items-center gap-2 px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
                <i className="ri-sparkling-2-line text-amber-300 text-sm"></i>
                <span className="text-sm font-semibold">Powered by OpenClaw Algorithm</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
                <i className="ri-award-line text-sm"></i>
                <span className="text-sm font-semibold">Studio-Quality Slides</span>
              </div>
            </div>
            <h1 className="text-5xl font-extrabold mb-5 leading-tight">
              AI Presentation<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-pink-300">Creator</span>
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Create stunning, professional presentations with AI. Choose from 3,000+ templates, generate slide outlines in seconds, and export to any format instantly.
            </p>
            <div className="flex flex-wrap gap-3 mb-10">
              <button onClick={() => setShowLogin(true)} className="flex items-center gap-2.5 px-7 py-3.5 bg-white text-rose-600 font-bold rounded-full hover:bg-rose-50 transition-all cursor-pointer whitespace-nowrap text-sm">
                <i className="ri-magic-line text-base"></i>Create Presentation — Free
              </button>
              <button onClick={() => setShowAIOutlineModal(true)} className="flex items-center gap-2.5 px-7 py-3.5 bg-white/15 backdrop-blur-sm border border-white/40 text-white font-bold rounded-full hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap text-sm">
                <i className="ri-sparkling-2-line text-base"></i>Generate AI Outline
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-6 text-sm text-white/75">
              <span className="flex items-center gap-1.5"><i className="ri-check-line text-emerald-300"></i>No design skills needed</span>
              <span className="flex items-center gap-1.5"><i className="ri-check-line text-emerald-300"></i>Export to PowerPoint</span>
              <span className="flex items-center gap-1.5"><i className="ri-check-line text-emerald-300"></i>Free plan forever</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats Bar ── */}
      <div
        ref={statsEntrance.ref}
        className={`transition-all duration-700 ease-out ${statsEntrance.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.22,1,0.36,1)' }}
      >
        <PresentationStatsBar />
      </div>

      {/* ── Feature Highlights ── */}
      <div
        ref={(el) => {
          (featuresEntrance.ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
          (featuresRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        }}
        className={`transition-all duration-700 ease-out ${featuresEntrance.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.22,1,0.36,1)' }}
      >
        <PresentationFeatureHighlights
          onCreatePresentation={() => setShowLogin(true)}
          onBrowseTemplates={scrollToTemplates}
          onLogin={() => setShowLogin(true)}
        />
      </div>

      {/* ── Trending Topics ── */}
      <div
        ref={trendingEntrance.ref}
        className={`transition-all duration-700 ease-out ${trendingEntrance.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.22,1,0.36,1)' }}
      >
        <PresentationTrendingTopics
          onTopicSearch={handleTopicSearch}
          onCreateFromTopic={() => setShowLogin(true)}
        />
      </div>

      {/* ── Template Browser ── */}
      <div ref={templatesSectionRef} className="max-w-7xl mx-auto px-6 pt-12 pb-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">Browse Templates</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {filteredTemplates.length.toLocaleString()} templates
              {selectedCategory !== 'All' ? ` in ${selectedCategory}` : ''}
              {searchQuery ? ` matching "${searchQuery}"` : ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Generate AI Outline button */}
            <button
              onClick={() => setShowAIOutlineModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-sm font-bold rounded-xl hover:opacity-90 cursor-pointer transition-all whitespace-nowrap"
            >
              <i className="ri-sparkling-2-line text-sm"></i>Generate AI Outline
            </button>
            {compareList.length > 0 && (
              <button
                onClick={() => setCompareList([])}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 text-rose-500 text-xs font-semibold rounded-xl border border-rose-200 hover:bg-rose-100 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-close-line"></i>Clear ({compareList.length})
              </button>
            )}
            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
              <i className="ri-sort-desc text-slate-400"></i>Sort:
            </div>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-300/20 transition-colors"
            >
              {SORT_OPTIONS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>

        {/* Search */}
        <div className="max-w-2xl mb-6">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg"></i>
            <input
              type="text"
              placeholder="Search by name, category, or style..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="w-full pl-12 pr-32 py-3.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-300/20 transition-colors"
            />
            <button
              onClick={() => setShowAttach(!showAttach)}
              className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-attachment-line"></i>Attach
              {attachedFiles.length > 0 && <span className="px-1.5 py-0.5 bg-rose-500 text-white text-xs font-bold rounded-full">{attachedFiles.length}</span>}
            </button>
          </div>
          {/* Attach panel */}
          {showAttach && (
            <div className="mt-3 bg-white border border-slate-200 rounded-2xl p-5">
              <div
                onDrop={e => { e.preventDefault(); setIsDragging(false); addFiles(Array.from(e.dataTransfer.files)); }}
                onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${isDragging ? 'border-rose-400 bg-rose-50' : 'border-slate-200 bg-slate-50'}`}
              >
                <i className={`ri-upload-cloud-line text-3xl mb-2 ${isDragging ? 'text-rose-500' : 'text-slate-300'}`}></i>
                <p className="text-sm text-slate-500 mb-3">Drag &amp; drop files, or click to browse</p>
                <input type="file" multiple id="present-file-upload" className="hidden" onChange={e => addFiles(Array.from(e.target.files ?? []))} />
                <label htmlFor="present-file-upload" className="px-5 py-2 bg-rose-500 hover:bg-rose-600 text-white text-sm font-bold rounded-lg cursor-pointer transition-colors">Choose Files</label>
              </div>
              {attachedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  {attachedFiles.map(f => (
                    <div key={f.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                      {f.preview ? <img src={f.preview} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" /> : <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center flex-shrink-0"><i className="ri-file-line text-rose-500"></i></div>}
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-800 truncate">{f.file.name}</p><p className="text-xs text-slate-400">{formatSize(f.file.size)}</p></div>
                      <button onClick={() => setAttachedFiles(p => p.filter(x => x.id !== f.id))} className="text-slate-400 hover:text-red-500 cursor-pointer"><i className="ri-close-line"></i></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Category Tabs */}
        <div className="relative mb-3">
          {catCanLeft && (
            <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-slate-50 to-transparent z-10 flex items-center pointer-events-none">
              <button onClick={() => scrollCat('left')} className="w-8 h-8 bg-white border border-slate-200 rounded-full flex items-center justify-center cursor-pointer pointer-events-auto"><i className="ri-arrow-left-s-line text-slate-600"></i></button>
            </div>
          )}
          {catCanRight && (
            <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-slate-50 to-transparent z-10 flex items-center justify-end pointer-events-none">
              <button onClick={() => scrollCat('right')} className="w-8 h-8 bg-white border border-slate-200 rounded-full flex items-center justify-center cursor-pointer pointer-events-auto"><i className="ri-arrow-right-s-line text-slate-600"></i></button>
            </div>
          )}
          <div ref={categoriesRef} className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`flex-shrink-0 px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === cat
                    ? 'bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white'
                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Slide Count Range Quick-Filter */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-1.5 mr-1">
            <i className="ri-layout-grid-line text-slate-400 text-sm"></i>
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Slide Count</span>
          </div>
          {SLIDE_RANGES.map((range, idx) => (
            <button
              key={range.label}
              onClick={() => handleSlideRangeChange(idx)}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap cursor-pointer border ${
                slideRangeIdx === idx
                  ? 'bg-rose-500/10 border-rose-400/60 text-rose-600'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-rose-300 hover:text-rose-500 hover:bg-rose-50/50'
              }`}
            >
              {slideRangeIdx === idx && <i className="ri-check-line text-xs"></i>}
              {range.label}
            </button>
          ))}
          {slideRangeIdx !== 0 && (
            <button
              onClick={() => handleSlideRangeChange(0)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium text-slate-400 hover:text-slate-600 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-close-line text-xs"></i>Clear
            </button>
          )}
        </div>

        {/* ── Infographic Type Quick-Filter ── */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-1.5 mr-1">
            <i className="ri-pie-chart-2-line text-slate-400 text-sm"></i>
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Infographic Type</span>
          </div>
          {INFOGRAPHIC_TYPES.map(type => {
            const isActive = selectedInfographic === type.id;
            return (
              <button
                key={type.id}
                onClick={() => handleInfographicTypeChange(type.id)}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap cursor-pointer border ${
                  isActive
                    ? 'text-white border-transparent'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                }`}
                style={isActive ? { backgroundColor: type.color, borderColor: type.color } : {}}
              >
                <i className={`${type.icon} text-xs`} style={isActive ? {} : { color: type.color }}></i>
                {type.label}
                {isActive && <i className="ri-check-line text-xs"></i>}
              </button>
            );
          })}
          {selectedInfographic !== 'all' && (
            <button onClick={() => handleInfographicTypeChange('all')} className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium text-slate-400 hover:text-slate-600 cursor-pointer transition-colors whitespace-nowrap">
              <i className="ri-close-line text-xs"></i>Clear
            </button>
          )}
        </div>

        {/* ── Color Palette Quick-Filter ── */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <div className="flex items-center gap-1.5 mr-1">
            <i className="ri-palette-line text-slate-400 text-sm"></i>
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Color Palette</span>
          </div>
          {COLOR_PALETTES.map(palette => {
            const isActive = selectedPalette === palette.id;
            return (
              <button
                key={palette.id}
                onClick={() => handlePaletteChange(palette.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap cursor-pointer border ${
                  isActive
                    ? 'bg-slate-900 border-slate-900 text-white'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex gap-0.5">
                  {palette.swatches.map((c, i) => (
                    <div key={i} className="w-3 h-3 rounded-full border border-white/60" style={{ backgroundColor: c }} />
                  ))}
                </div>
                {palette.label}
                {isActive && <i className="ri-check-line text-xs"></i>}
              </button>
            );
          })}
          {selectedPalette !== 'all' && (
            <button onClick={() => handlePaletteChange('all')} className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium text-slate-400 hover:text-slate-600 cursor-pointer transition-colors whitespace-nowrap">
              <i className="ri-close-line text-xs"></i>Clear
            </button>
          )}
        </div>

        {/* ── Active Filters Summary Bar ── */}
        {activeFilterChips.length > 0 && (
          <div className="sticky top-[72px] z-30 mb-5 -mx-2 px-2 py-0.5">
            <div className="flex items-center gap-2 flex-wrap px-4 py-3 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-2xl shadow-sm">
              <div className="flex items-center gap-1.5 flex-shrink-0 mr-1">
                <i className="ri-filter-3-line text-rose-500 text-sm"></i>
                <span className="text-xs font-extrabold text-slate-600 uppercase tracking-wider">Active Filters</span>
                <span className="px-2 py-0.5 bg-rose-50 text-rose-600 text-xs font-bold rounded-full border border-rose-100">{activeFilterChips.length}</span>
              </div>
              {activeFilterChips.map(chip => (
                <button
                  key={chip.key}
                  onClick={chip.onClear}
                  className="flex items-center gap-1.5 pl-3 pr-2 py-1 bg-slate-900 text-white text-xs font-semibold rounded-full hover:bg-slate-700 cursor-pointer transition-colors whitespace-nowrap group/chip"
                >
                  <i className={`${chip.icon} text-[10px] text-slate-300`}></i>
                  <span>{chip.label}</span>
                  <span className="w-4 h-4 bg-white/20 group-hover/chip:bg-white/30 rounded-full flex items-center justify-center flex-shrink-0 transition-colors">
                    <i className="ri-close-line text-[10px]"></i>
                  </span>
                </button>
              ))}
              <button
                onClick={clearAllFilters}
                className="ml-auto flex items-center gap-1.5 px-3 py-1 bg-rose-500 text-white text-xs font-bold rounded-full hover:bg-rose-600 cursor-pointer transition-colors whitespace-nowrap flex-shrink-0"
              >
                <i className="ri-close-circle-line text-xs"></i>Clear All
              </button>
            </div>
          </div>
        )}

        {/* Recently Viewed shelf */}
        <RecentlyViewedShelf
          ids={recentlyViewed}
          allTemplates={presentationTemplates}
          onPreview={t => { setPreviewTemplate(t); addToRecentlyViewed(t.id); }}
          onUse={() => setShowLogin(true)}
          onClear={clearRecentlyViewed}
        />

        {/* Collections shelf */}
        <CollectionsShelf
          allTemplates={presentationTemplates}
          onApplyCollection={handleApplyCollection}
        />

        {/* Compare hint */}
        {compareList.length === 0 && (
          <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-rose-50 border border-rose-100 rounded-xl w-fit">
            <i className="ri-scales-3-line text-rose-400 text-sm"></i>
            <p className="text-xs text-rose-600 font-medium">Hover a template card and click <strong>Compare</strong> to compare up to 3 templates side-by-side.</p>
          </div>
        )}

        {/* Template Grid */}
        <div
          className={`transition-all duration-500 ease-out ${isGridLoading ? 'opacity-60 scale-[0.99]' : 'opacity-100 scale-100'}`}
          style={{ transitionTimingFunction: 'cubic-bezier(0.22,1,0.36,1)' }}
        >
          {isGridLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {Array.from({ length: TEMPLATES_PER_PAGE }).map((_, i) => (
                <div key={i} className="animate-fade-in" style={{ animationDelay: `${i * 30}ms` }}>
                  <TemplateCardSkeleton />
                </div>
              ))}
            </div>
          ) : currentTemplates.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {currentTemplates.map((t, idx) => (
                <div key={t.id} className="animate-fade-in" style={{ animationDelay: `${idx * 40}ms` }}>
                  <TemplateCard
                    template={t}
                    onPreview={() => { setPreviewTemplate(t); addToRecentlyViewed(t.id); }}
                    onUse={() => { addToRecentlyViewed(t.id); handleUseAction(); }}
                    onCompareToggle={() => handleCompareToggle(t.id)}
                    isComparing={compareList.includes(t.id)}
                    isFavorited={favorites.includes(t.id)}
                    onFavoriteToggle={() => toggleFavorite(t.id)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-24">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-2xl text-slate-400"></i>
              </div>
              <p className="text-lg font-bold text-slate-700 mb-2">No templates found</p>
              <p className="text-sm text-slate-500 mb-4">Try a different search or category</p>
              <button onClick={clearAllFilters} className="px-5 py-2.5 bg-rose-500 text-white font-bold rounded-xl cursor-pointer hover:bg-rose-600 transition-colors whitespace-nowrap">
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            <button
              onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: templatesSectionRef.current?.offsetTop ?? 0, behavior: 'smooth' }); }}
              disabled={currentPage === 1}
              className="w-10 h-10 rounded-xl border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
              let p: number;
              if (totalPages <= 7) { p = i + 1; }
              else if (currentPage <= 4) { p = i + 1; }
              else if (currentPage >= totalPages - 3) { p = totalPages - 6 + i; }
              else { p = currentPage - 3 + i; }
              return (
                <button
                  key={p}
                  onClick={() => { setCurrentPage(p); window.scrollTo({ top: templatesSectionRef.current?.offsetTop ?? 0, behavior: 'smooth' }); }}
                  className={`w-10 h-10 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                    p === currentPage
                      ? 'bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white'
                      : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {p}
                </button>
              );
            })}
            <button
              onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: templatesSectionRef.current?.offsetTop ?? 0, behavior: 'smooth' }); }}
              disabled={currentPage === totalPages}
              className="w-10 h-10 rounded-xl border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
            <span className="text-xs text-slate-400 ml-2 whitespace-nowrap">Page {currentPage} of {totalPages.toLocaleString()}</span>
          </div>
        )}
      </div>

      {/* ── Publish & Share Banner ── */}
      <div
        ref={bannerEntrance.ref}
        className={`mt-12 transition-all duration-700 ease-out ${bannerEntrance.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.22,1,0.36,1)' }}
      >
        <PresentationPublishBanner onSignUp={() => setShowLogin(true)} />
      </div>

      {/* ── Final CTA ── */}
      <div className="bg-gradient-to-r from-rose-600 via-pink-600 to-fuchsia-600 py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/20 border border-white/30 rounded-full mb-6">
            <i className="ri-slideshow-line text-white text-sm"></i>
            <span className="text-sm font-semibold text-white">Join 300K+ Creators</span>
          </div>
          <h2 className="text-4xl font-extrabold text-white mb-4 leading-tight">
            Your next presentation is<br />30 seconds away
          </h2>
          <p className="text-lg text-white/85 max-w-xl mx-auto mb-8 leading-relaxed">
            Pick a template, describe your topic, and let openclaw.ai do the rest. No design skills needed. Free plan included.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button onClick={() => setShowLogin(true)} className="flex items-center gap-2.5 px-8 py-4 bg-white text-rose-600 font-extrabold rounded-full hover:bg-rose-50 transition-all cursor-pointer whitespace-nowrap text-base">
              <i className="ri-magic-line text-lg"></i>Start Creating — It&apos;s Free
            </button>
            <button onClick={scrollToTemplates} className="flex items-center gap-2.5 px-8 py-4 bg-white/15 backdrop-blur-sm border border-white/40 text-white font-bold rounded-full hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap text-base">
              <i className="ri-layout-grid-line text-lg"></i>Browse Templates
            </button>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-8 text-sm text-white/70">
            <span className="flex items-center gap-2"><i className="ri-check-double-line text-emerald-300"></i>No credit card required</span>
            <span className="flex items-center gap-2"><i className="ri-check-double-line text-emerald-300"></i>3 free presentations</span>
            <span className="flex items-center gap-2"><i className="ri-check-double-line text-emerald-300"></i>Export to PowerPoint</span>
            <span className="flex items-center gap-2"><i className="ri-check-double-line text-emerald-300"></i>Cancel anytime</span>
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="bg-rose-50 border-t border-rose-100 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-rose-500 to-fuchsia-500 rounded-lg flex items-center justify-center">
              <i className="ri-slideshow-line text-white text-sm"></i>
            </div>
            <span className="text-sm font-bold text-slate-700">openclaw.ai Presentation Creator</span>
          </div>
          <p className="text-xs text-slate-500">© 2026 openclaw.ai · Powered by the OpenClaw Algorithm</p>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <a href="#" className="hover:text-slate-700 transition-colors">Privacy</a>
            <a href="#" className="hover:text-slate-700 transition-colors">Terms</a>
            <a href="#" className="hover:text-slate-700 transition-colors">Help</a>
          </div>
        </div>
      </footer>

      {/* ── Floating Compare Bar ── */}
      {compareList.length >= 2 && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-5 py-3 bg-slate-900 rounded-2xl shadow-2xl border border-slate-700">
          <div className="flex items-center gap-2">
            {compareTemplates.map(t => (
              <div key={t.id} className="relative w-10 h-10 rounded-lg overflow-hidden border-2 border-rose-400">
                <img src={t.thumbnail} alt={t.name} className="w-full h-full object-cover" />
                <button
                  onClick={() => handleCompareToggle(t.id)}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 rounded-full flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-close-line text-white text-[8px]"></i>
                </button>
              </div>
            ))}
            {compareList.length < 3 && (
              <div className="w-10 h-10 rounded-lg border-2 border-dashed border-slate-600 flex items-center justify-center">
                <i className="ri-add-line text-slate-500 text-sm"></i>
              </div>
            )}
          </div>
          <div className="w-px h-8 bg-slate-700"></div>
          <span className="text-sm font-medium text-slate-300 whitespace-nowrap">{compareList.length} selected</span>
          <button
            onClick={() => setShowCompareModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white text-sm font-bold rounded-xl hover:opacity-90 cursor-pointer transition-all whitespace-nowrap"
          >
            <i className="ri-scales-3-line"></i>Compare Now
          </button>
        </div>
      )}

      {/* ── Floating Favorites FAB ── */}
      <button
        onClick={() => setShowFavoritesDrawer(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-white border-2 border-rose-200 text-rose-600 text-sm font-bold rounded-2xl shadow-lg hover:border-rose-400 hover:bg-rose-50 cursor-pointer transition-all whitespace-nowrap"
      >
        <i className="ri-heart-fill text-rose-500 text-base"></i>
        Saved
        {favorites.length > 0 ? (
          <span className="px-2 py-0.5 bg-rose-500 text-white text-xs font-extrabold rounded-full">{favorites.length}</span>
        ) : (
          <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs font-bold rounded-full">0</span>
        )}
      </button>

      {/* ── Generate AI Prompt Bar ── */}
      <GenerateAIPromptBar onUseOutline={() => setShowLogin(true)} />

      {/* ── Modals ── */}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      {showUpgradeTrial && (
        <UpgradeTrialModal
          toolName="Presentation Creator"
          usedCount={trialUsed}
          onSignIn={() => { setShowUpgradeTrial(false); setShowLogin(true); }}
          onClose={() => setShowUpgradeTrial(false)}
        />
      )}
      {previewTemplate && (
        <SlidePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onUse={() => { setPreviewTemplate(null); handleUseAction(); }}
          onSwitchTemplate={t => { addToRecentlyViewed(t.id); setPreviewTemplate(t); }}
        />
      )}
      {showCompareModal && compareTemplates.length >= 2 && (
        <CompareTemplatesModal
          templates={compareTemplates}
          onClose={() => setShowCompareModal(false)}
          onSelectTemplate={() => { setShowCompareModal(false); handleUseAction(); }}
        />
      )}
      {showFavoritesDrawer && (
        <FavoritesDrawer
          favorites={favorites}
          allTemplates={presentationTemplates}
          onClose={() => setShowFavoritesDrawer(false)}
          onUnfavorite={toggleFavorite}
          onPreview={t => { setShowFavoritesDrawer(false); setPreviewTemplate(t); addToRecentlyViewed(t.id); }}
          onUse={() => { setShowFavoritesDrawer(false); handleUseAction(); }}
        />
      )}
      {showAIOutlineModal && (
        <GenerateAIOutlineModal
          onClose={() => setShowAIOutlineModal(false)}
          onUseOutline={() => { setShowAIOutlineModal(false); handleUseAction(); }}
        />
      )}
    </div>
  );
}
